import { motion } from "framer-motion";
import { Leaf, RefreshCcw, ShieldCheck, Sparkles, Star, Truck } from "lucide-react";
import { IMAGES } from "../data/site";

const easeOut = [0.16, 1, 0.3, 1];

const VALUES = [
  {
    icon: Leaf,
    color: "bg-mint-50 text-mint-600",
    title: "Organic-soft fabrics",
    text: "Gentle, breathable cotton that keeps up with cartwheels and nap time.",
  },
  {
    icon: ShieldCheck,
    color: "bg-lav-50 text-lav-600",
    title: "Skin-friendly & safe",
    text: "Dermatologist-approved, tag-free and seam-free comfort for sensitive skin.",
  },
  {
    icon: Truck,
    color: "bg-baby-50 text-baby-600",
    title: "Fast, tracked delivery",
    text: "Free standard shipping on orders over £50, with live tracking updates.",
  },
  {
    icon: RefreshCcw,
    color: "bg-blush-50 text-blush-600",
    title: "30-day happy returns",
    text: "A prepaid label in every box — no questions, no fuss, no lost naps.",
  },
];

export default function BrandHighlights() {
  return (
    <section className="py-20 md:py-24" data-testid="brand-highlights-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: easeOut }}
          className="relative mx-auto w-full max-w-[420px]"
        >
          <div className="overflow-hidden rounded-t-[220px] rounded-b-[36px] border-8 border-white shadow-2xl shadow-blush-100">
            <img
              src={IMAGES.brand}
              alt="Girl twirling in a Sparkwear dress"
              loading="lazy"
              className="h-[440px] w-full object-cover md:h-[520px]"
            />
          </div>
          <div className="absolute -bottom-5 -right-2 animate-float-slow rounded-2xl bg-white p-4 shadow-xl shadow-lav-100 md:-right-8">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-butter-500 text-butter-500" />
              ))}
            </div>
            <p className="mt-1.5 font-display text-sm font-semibold text-ink">
              Loved by parents
            </p>
          </div>
          <Sparkles className="absolute -left-3 top-10 h-7 w-7 animate-twinkle text-lav-500" />
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: easeOut }}
          >
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-blush-700">
              Why Sparkwear
            </p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl md:text-5xl">
              Made for play. Designed to shine.
            </h2>
            <p className="mt-4 max-w-lg leading-relaxed text-muted">
              Every Sparkwear piece is built for real childhood — soft enough
              for newborn skin, tough enough for the playground, and pretty
              enough for the family photo album.
            </p>
          </motion.div>

          <div className="mt-10 space-y-6">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: easeOut, delay: i * 0.08 }}
                className="flex items-start gap-4"
              >
                <span
                  className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${v.color}`}
                >
                  <v.icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold text-ink">
                    {v.title}
                  </h3>
                  <p className="mt-0.5 text-sm leading-relaxed text-muted">
                    {v.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
